import React, { useState, useEffect } from 'react';
import { manageAPI, proxyImageUrl } from '../api/client';
import { formatKyivDate, toUtcDate } from '../utils/kyivTime';
import ProductModal from './ProductModal';

export default function StaleProductsTab() {
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const load = async (m, off = 0) => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await manageAPI.getStaleProducts(m, off);
      setData(res.data);
      setOffset(off);
    } catch { setLoadError(true); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(months, 0); }, []);

  const handleMonthsChange = (val) => {
    setMonths(val);
    load(val, 0);
  };

  // last_in_stock_at = null означає НЕ "щойно", а навпаки — товар ні разу не
  // бачили в наявності з моменту появи цього поля (suppliers_sync.py ставить
  // дату лише коли РЕАЛЬНО бачить наявність) — найстаріший/найневідоміший
  // випадок, тому не можна показувати як TODAY/"сегодня" (це вводило в
  // оману — виглядало б як щойно в наявності).
  const fmt = (iso) => {
    if (!iso) return 'неизвестно';
    return formatKyivDate(iso);
  };

  const ago = (iso) => {
    if (!iso) return 'нет данных о наличии — вероятно, очень давно';
    const days = Math.floor((Date.now() - toUtcDate(iso).getTime()) / 86400000);
    if (days < 1) return 'сегодня';
    if (days < 30) return `${days} дн. назад`;
    return `${Math.floor(days / 30)} мес. назад`;
  };

  const PAGE = 200;
  const totalPages = data ? Math.ceil(data.total / PAGE) : 0;
  const currentPage = Math.floor(offset / PAGE);

  return (
    <div className="stale-tab">
      <div className="stale-header">
        <div>
          <div className="stale-title">Товары под удаление</div>
          <div className="stale-desc">Товары, которые не были в наличии дольше указанного срока.</div>
        </div>
        <label className="stale-months-label">
          Не в наличии дольше
          <select
            className="stale-months-select"
            value={months}
            onChange={e => handleMonthsChange(Number(e.target.value))}
          >
            {[3, 6, 9, 12, 18, 24].map(m => (
              <option key={m} value={m}>{m} мес.</option>
            ))}
          </select>
        </label>
      </div>

      {loadError && (
        <div className="stale-loading">
          Ошибка загрузки.{' '}
          <button className="btn btn-sm btn-secondary" onClick={() => load(months, offset)}>Повторить</button>
        </div>
      )}
      {loading ? (
        <div className="stale-loading">Загрузка…</div>
      ) : !loadError && data && (
        <>
          <div className="stale-count">
            Найдено: <strong>{data.total}</strong> товаров
            {data.total > PAGE && (
              <span className="stale-page-info"> · страница {currentPage + 1} из {totalPages}</span>
            )}
          </div>

          {data.items.length === 0 ? (
            <div className="stale-empty">Таких товаров нет</div>
          ) : (
            <>
              <div className="stale-list">
                {data.items.map(p => (
                  <div key={p.sku} className="stale-item" onClick={() => setSelectedProduct(p)} style={{ cursor: 'pointer' }}>
                    <div className="stale-item-photo">
                      {p.photo_url
                        ? <img src={proxyImageUrl(p.photo_url)} alt="" loading="lazy" />
                        : <span className="stale-item-nophoto">—</span>
                      }
                    </div>
                    <code className="stale-item-sku">{p.sku}</code>
                    <span className="stale-item-name">{p.name}</span>
                    {p.season && <span className="stale-item-season">{p.season}</span>}
                    <span className="stale-item-date" title={ago(p.last_in_stock_at)}>
                      {fmt(p.last_in_stock_at)}
                    </span>
                  </div>
                ))}
              </div>

              {data.total > PAGE && (
                <div className="stale-pagination">
                  <button
                    className="stale-page-btn"
                    onClick={() => load(months, offset - PAGE)}
                    disabled={offset === 0}
                  >← Назад</button>
                  <span className="stale-page-num">{currentPage + 1} / {totalPages}</span>
                  <button
                    className="stale-page-btn"
                    onClick={() => load(months, offset + PAGE)}
                    disabled={offset + PAGE >= data.total}
                  >Вперёд →</button>
                </div>
              )}
            </>
          )}
        </>
      )}
      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onPrev={null}
          onNext={null}
        />
      )}
    </div>
  );
}
